import { Link } from "react-router-dom";
import { Phone, MessageSquare } from "lucide-react";
import { useTranslation } from "react-i18next";
import moeLogoUrl from "@/assets/brand/moe-logo.webp";

/**
 * Footer - Main footer for public pages matching mockup design
 * Includes MOE branding, contact info, link columns, and copyright
 */
function Footer() {
  const { t } = useTranslation("common");
  const currentYear = new Date().getFullYear();

  const footerSections = {
    services: {
      titleKey: "footer.allServices",
      links: [
        {
          to: "/services?category=transfers",
          labelKey: "footer.schoolTransfers",
        },
        {
          to: "/services?category=examinations",
          labelKey: "footer.examinations",
        },
        {
          to: "/services?category=certifications",
          labelKey: "footer.certifications",
        },
      ],
    },
    helpCenter: {
      titleKey: "footer.helpCenter",
      links: [
        { to: "/track-application", labelKey: "footer.trackApplication" },
        { to: "/help#faq", labelKey: "footer.faqs" },
        { to: "/guide", labelKey: "footer.userGuide" },
        { to: "/help#contact", labelKey: "footer.contactUs" },
      ],
    },
    about: {
      titleKey: "footer.aboutMoe",
      links: [
        { to: "/about#mission", labelKey: "footer.ourMission" },
        { to: "/privacy", labelKey: "footer.privacyPolicy" },
        { to: "/terms", labelKey: "footer.termsOfService" },
      ],
    },
  };

  return (
    <footer className="bg-dark-blue dark:bg-surface text-white">
      {/* Main Footer Content */}
      <div className="max-w-[1600px] mx-auto px-8 lg:px-12 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Ministry Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              {/* MOE Logo */}
              <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center overflow-hidden p-0.5">
                <img
                  src={moeLogoUrl}
                  alt="MOE Logo"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            <p className="text-white/90 font-semibold text-lg mb-1">
              {t("footer.ministryTitle")}
            </p>
            <p className="text-white/60 text-sm mb-6">{t("footer.platform")}</p>

            {/* Contact Info */}
            <div className="space-y-3">
              <a
                href="tel:+5922317230"
                className="flex items-center gap-3 text-white/90 hover:text-white transition-colors group"
              >
                <Phone
                  size={18}
                  className="text-secondary-green group-hover:scale-110 transition-transform"
                />
                <span className="text-sm">+592 231 7230</span>
              </a>
              <a
                href="tel:+5922368530"
                className="flex items-center gap-3 text-white/90 hover:text-white transition-colors group"
              >
                <MessageSquare
                  size={18}
                  className="text-secondary-green group-hover:scale-110 transition-transform"
                />
                <span className="text-sm">+592 236 8530</span>
              </a>
            </div>
          </div>

          {/* Services Links */}
          <div>
            <h3 className="font-semibold text-white mb-4">
              {t(footerSections.services.titleKey)}
            </h3>
            <ul className="space-y-3">
              {footerSections.services.links.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-white/90 hover:text-white hover:translate-x-1 transition-all duration-200 inline-block"
                  >
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Help Center Links */}
          <div>
            <h3 className="font-semibold text-white mb-4">
              {t(footerSections.helpCenter.titleKey)}
            </h3>
            <ul className="space-y-3">
              {footerSections.helpCenter.links.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-white/90 hover:text-white hover:translate-x-1 transition-all duration-200 inline-block"
                  >
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* About Links */}
          <div>
            <h3 className="font-semibold text-white mb-4">
              {t(footerSections.about.titleKey)}
            </h3>
            <ul className="space-y-3">
              {footerSections.about.links.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-white/90 hover:text-white hover:translate-x-1 transition-all duration-200 inline-block"
                  >
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar. The bottom padding clears the floating "Ask AI" button
          (fixed bottom-6, about 76px tall with its offset), which otherwise
          sat on the Privacy / Terms / Accessibility links at the end of every
          page, where there is no scrolling them out from under it. */}
      <div className="border-t border-outline-variant/15">
        <div className="max-w-[1600px] mx-auto px-8 lg:px-12 pt-4 pb-24">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/60">
              {t("footer.copyright", { year: currentYear })}
            </p>
            <div className="flex items-center gap-6">
              <Link
                to="/privacy"
                className="text-sm text-white/60 hover:text-white transition-colors"
              >
                {t("footer.privacy")}
              </Link>
              <Link
                to="/terms"
                className="text-sm text-white/60 hover:text-white transition-colors"
              >
                {t("footer.terms")}
              </Link>
              <Link
                to="/accessibility"
                className="text-sm text-white/60 hover:text-white transition-colors"
              >
                {t("footer.accessibility")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
